import { Inject, Injectable, Optional } from '@angular/core';
import { FirebaseApp } from 'firebase/app';
import { Firestore, doc, getFirestore, onSnapshot } from 'firebase/firestore';
import { Metadata } from 'aws-sdk/clients/s3';
import {
  CreateTempUploadSessionPayload,
  CreateTempUploadSessionPayloadKeys,
  CreateTempUploadSessionResponse,
  FunctionsNames,
  MediaSize,
  TempUploadStatus,
  UploadMediaPayload,
  UploadMediaPayloadKeys,
} from '@ag-common-lib/public-api';
import { CloudFunctionsService } from './cloud-functions.service';
import { FireStorageDao } from '../dao/FireStorage.dao';
import { FIREBASE_APP } from '../injections/firebase-app';
import { USE_TEMP_MEDIA_UPLOAD } from '../injections/use-temp-media-upload';

const DEFAULT_TEMP_UPLOAD_TIMEOUT_MS = 2 * 60 * 1000;
const ALWAYS_TEMP_UPLOAD_PATHS = new Set(['template-builder']);

export interface MediaUploadRequest {
  filePath: string;
  contentType: string;
  fileId?: string;
  withThumbnail?: boolean;
  metadata?: Metadata;
  file?: File;
  base64?: string;
  forceLegacy?: boolean;
  onProgress?: (progress: number) => void;
  timeoutMs?: number;
  timingStart?: number;
  /** When true, temp uploads resolve only after media processing finishes and the asset is readable. */
  waitForProcessing?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class MediaUploadService {
  private readonly firestore: Firestore;
  private readonly useTempMediaUpload: boolean;

  constructor(
    private readonly cloudFunctions: CloudFunctionsService,
    private readonly fireStorageDao: FireStorageDao,
    @Inject(FIREBASE_APP) private readonly firebaseApp: FirebaseApp,
    @Optional() @Inject(USE_TEMP_MEDIA_UPLOAD) useTempMediaUpload?: boolean,
  ) {
    this.firestore = getFirestore(firebaseApp);
    this.useTempMediaUpload = !!useTempMediaUpload;
  }

  uploadMedia = async (request: MediaUploadRequest): Promise<string> => {
    // Force temp strategy when path is in ALWAYS_TEMP_UPLOAD_PATHS (fix for email template builder that is loaded through a different module tree than template builder)
    const normalizedFilePath = String(request.filePath ?? '')
      .trim()
      .replace(/^\/+|\/+$/g, '');
    const useTempForPath = ALWAYS_TEMP_UPLOAD_PATHS.has(normalizedFilePath);
    const useTemp = !request.forceLegacy && (this.useTempMediaUpload || useTempForPath);
    const start = request.timingStart ?? this.getTimingNow();
    const fileName = (request.metadata as any)?.fileName ?? request.file?.name;
    const baseContext = {
      strategy: useTemp ? 'temp' : 'legacy',
      fileName,
      filePath: request.filePath,
      contentType: request.contentType,
      fileSize: request.file?.size,
    };

    if (!useTemp) {
      return this.uploadMediaLegacy(request, start, baseContext);
    }

    return this.uploadMediaTemp(request, start, baseContext);
  };

  uploadMediaLegacy = async (
    request: MediaUploadRequest,
    start: number,
    baseContext: Record<string, any>,
  ): Promise<string> => {
    try {
      const base64Start = this.getTimingNow();
      const mediaData = await this.resolveBase64(request);
      this.logTiming('legacy.base64', base64Start, baseContext);

      const payload: UploadMediaPayload = {
        [UploadMediaPayloadKeys.fileId]: request.fileId,
        [UploadMediaPayloadKeys.filePath]: request.filePath,
        [UploadMediaPayloadKeys.mediaData]: mediaData,
        [UploadMediaPayloadKeys.contentType]: request.contentType,
        [UploadMediaPayloadKeys.withThumbnail]: request.withThumbnail,
        [UploadMediaPayloadKeys.metadata]: request.metadata,
      };

      const uploadStart = this.getTimingNow();
      const result = await this.cloudFunctions.uploadMedia(payload);
      this.logTiming('legacy.upload', uploadStart, baseContext);
      this.logTiming('legacy.total', start, baseContext);
      return result;
    } catch (error) {
      this.logTiming('legacy.error', start, { ...baseContext, error: error?.message ?? error });
      throw error;
    }
  };

  private uploadMediaTemp = async (
    request: MediaUploadRequest,
    start: number,
    baseContext: Record<string, any>,
  ): Promise<string> => {
    try {
      const sessionStart = this.getTimingNow();
      const payload: CreateTempUploadSessionPayload = {
        [CreateTempUploadSessionPayloadKeys.filePath]: request.filePath,
        [CreateTempUploadSessionPayloadKeys.contentType]: request.contentType,
        [CreateTempUploadSessionPayloadKeys.fileId]: request.fileId,
        [CreateTempUploadSessionPayloadKeys.withThumbnail]: request.withThumbnail,
        [CreateTempUploadSessionPayloadKeys.metadata]: request.metadata,
      };

      const session = await this.cloudFunctions.createTempUploadSession(payload);
      this.logTiming('temp.session', sessionStart, { ...baseContext, uploadId: session.uploadId });

      const filePrepStart = this.getTimingNow();
      const file = await this.resolveFile(request, session);
      this.logTiming('temp.filePrep', filePrepStart, {
        ...baseContext,
        uploadId: session.uploadId,
        fileSize: file.size,
      });

      const uploadStart = this.getTimingNow();
      await this.uploadFileToTempStorage(session, file, request.onProgress);
      this.logTiming('temp.storageUpload', uploadStart, { ...baseContext, uploadId: session.uploadId });

      const sizesToWarm: MediaSize[] = [MediaSize.origin, MediaSize.small, MediaSize.medium, MediaSize.large];
      sizesToWarm.forEach(size => {
        void this.prewarmMediaCache({
          uploadId: session.uploadId,
          wasabiKey: session.wasabiKey,
          file,
          contentType: request.contentType,
          mediaSize: size,
        });
      });

      const processingStart = this.getTimingNow();
      const processingPromise = this.waitForTempUpload(
        session.uploadId,
        request.timeoutMs ?? DEFAULT_TEMP_UPLOAD_TIMEOUT_MS,
      )
        .then(() => {
          this.logTiming('temp.processing', processingStart, { ...baseContext, uploadId: session.uploadId });
        })
        .catch(error => {
          this.logTiming('temp.processing.error', processingStart, {
            ...baseContext,
            uploadId: session.uploadId,
            error: error?.message ?? error,
          });
          throw error;
        });

      if (request.waitForProcessing) {
        await processingPromise;
        this.logTiming('temp.total', start, { ...baseContext, uploadId: session.uploadId });
        return session.wasabiKey;
      }

      void processingPromise.catch(() => undefined);
      this.logTiming('temp.total', start, { ...baseContext, uploadId: session.uploadId });

      return session.wasabiKey;
    } catch (error) {
      this.logTiming('temp.error', start, { ...baseContext, error: error?.message ?? error });
      throw error;
    }
  };

  private uploadFileToTempStorage = async (
    session: CreateTempUploadSessionResponse,
    file: File,
    onProgress?: (progress: number) => void,
  ): Promise<void> => {
    const uploadTask = this.fireStorageDao.uploadFileResumable(file, session.tempPath);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        snapshot => {
          if (!onProgress) {
            return;
          }
          const totalBytes = snapshot.totalBytes || 1;
          const progress = Math.min(1, snapshot.bytesTransferred / totalBytes);
          onProgress(progress);
        },
        error => reject(error),
        () => resolve(),
      );
    });
  };

  private waitForTempUpload = async (uploadId: string, timeoutMs: number): Promise<void> => {
    const docRef = doc(this.firestore, 'media-uploads', uploadId);

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error('Upload processing timeout'));
      }, timeoutMs);

      const unsubscribe = onSnapshot(
        docRef,
        snapshot => {
          const data = snapshot.data() as { status?: TempUploadStatus; error?: string } | undefined;
          const status = data?.status;

          if (status === TempUploadStatus.done) {
            clearTimeout(timeoutId);
            unsubscribe();
            resolve();
            return;
          }

          if (status === TempUploadStatus.error) {
            clearTimeout(timeoutId);
            unsubscribe();
            reject(new Error(data?.error ?? 'Upload failed'));
          }
        },
        error => {
          clearTimeout(timeoutId);
          unsubscribe();
          reject(error);
        },
      );
    });
  };

  private resolveBase64 = async (request: MediaUploadRequest): Promise<string> => {
    const rawBase64 = request.base64 ?? (request.file ? await this.fileToBase64(request.file) : '');
    const base64 = this.stripBase64Prefix(rawBase64);

    if (!base64) {
      throw new Error('Media payload is empty');
    }

    return base64;
  };

  private resolveFile = async (request: MediaUploadRequest, session: CreateTempUploadSessionResponse): Promise<File> => {
    const file = request.file ?? (request.base64 ? this.base64ToFile(request.base64, request) : null);

    if (!file) {
      throw new Error('File is required for temp upload');
    }

    if (file.size > session.maxBytes) {
      throw new Error(`File size exceeds limit (${session.maxBytes} bytes)`);
    }

    return file;
  };

  private fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });

  private stripBase64Prefix = (value: string): string => {
    if (!value) {
      return '';
    }
    const parts = value.split(',');
    return parts.length > 1 ? parts[1] : value;
  };

  private base64ToFile = (value: string, request: MediaUploadRequest): File => {
    const base64 = this.stripBase64Prefix(value);
    const byteString = atob(base64);
    const byteArray = new Uint8Array(byteString.length);

    for (let i = 0; i < byteString.length; i += 1) {
      byteArray[i] = byteString.charCodeAt(i);
    }

    const fileName = request.fileId ?? 'upload';
    return new File([byteArray], fileName, { type: request.contentType });
  };

  private getTimingNow = (): number => {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now();
    }
    return Date.now();
  };

  private logTiming = (event: string, start: number, details: Record<string, any>) => {
    const durationMs = Math.round(this.getTimingNow() - start);
    const payload = { event, durationMs, ...details };
    const prefix = '[MediaUploadTiming]';

    if (event.endsWith('error')) {
      console.warn(prefix, payload);
      return;
    }

    console.info(prefix, payload);
  };

  private prewarmMediaCache = async (params: {
    uploadId: string;
    wasabiKey: string;
    file?: File;
    contentType?: string;
    mediaSize: MediaSize;
  }): Promise<void> => {
    try {
      if (typeof window === 'undefined' || !params?.wasabiKey) {
        return;
      }

      if (params.contentType && !params.contentType.startsWith('image/')) {
        return;
      }

      const baseUrl = this.cloudFunctions.getFunctionUrl(FunctionsNames.getMedia);
      const canonicalUrl = this.buildMediaUrl(baseUrl, params.wasabiKey, params.mediaSize);
      let blob: Blob | null = params.file ?? null;
      let contentType = params.contentType ?? params.file?.type ?? null;

      if (!blob) {
        const tempUrl = this.buildMediaUrl(baseUrl, params.wasabiKey, params.mediaSize, params.uploadId);
        const response = await fetch(tempUrl, { cache: 'no-store' });
        if (!response.ok) {
          return;
        }
        blob = await response.blob();
        contentType = response.headers.get('content-type') ?? contentType;
      }

      if (!blob) {
        return;
      }

      const payload = { type: 'CACHE_MEDIA', url: canonicalUrl, blob, contentType };
      if ('serviceWorker' in navigator && navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage(payload);
        return;
      }

      if ('caches' in window) {
        const cache = await caches.open('media-v1');
        const response = new Response(blob, {
          headers: {
            'Content-Type': contentType ?? blob.type ?? 'application/octet-stream',
            'Cache-Control': 'public, max-age=86400',
          },
        });
        await cache.put(canonicalUrl, response);
      }
    } catch (error) {}
  };

  private buildMediaUrl = (
    baseUrl: string,
    wasabiKey: string,
    mediaSize: MediaSize,
    uploadId?: string,
  ): string => {
    const url = new URL(`${baseUrl}/${wasabiKey}/${mediaSize}`);
    if (uploadId) {
      url.searchParams.set('uploadId', uploadId);
    }
    return url.toString();
  };
}
